/**
 * 权限策略基类
 *
 * 提供通用的权限检查逻辑，子类只需要提供：
 * 1. featureId
 * 2. 用量检查方法
 */

import { CreditService } from '@/domains/credit/services/credit-service';
import { UsageService } from '@/domains/credit/services/usage-service';
import { PermissionModel } from '@/domains/permission/models/permission-model';
import { CommandRegistry, Logger, Message } from '@patsnap/tahiti-core';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type {
    FeaturePermissionStrategy,
    FeaturePermissionContext,
    PermissionCheckResult,
    FeatureKey,
} from '@telecom/shared/domain/permission';
import { AiAgentEnvService } from '@telecom/shared/services/ai-agent-env-service/ai-agent-env-service';
import { HeaderService } from '@telecom/shared/services/header-service/header-service';
import { I18nService } from '@telecom/shared/services/i18n';
import { RavenService } from '@telecom/shared/services/raven-service/raven-service';
import * as TrackEvents from '@/constants/track-events';
import { TRACK_EVENT_TYPE } from '@/constants';

/**
 * 权限策略基类
 *
 * 实现了通用的权限检查逻辑：
 * 1. 如果有剩余用量 → 允许访问
 * 2. 如果是 FREE 用户 → 拒绝并打开定价弹窗
 * 3. 如果是 PRO 用户 → 拒绝并打开联系销售弹窗
 * 4. 其他情况 → 允许访问
 */
@injectable()
export abstract class BasePermissionStrategy implements FeaturePermissionStrategy {
    abstract readonly featureId: FeatureKey;

    @inject(UsageService)
    private readonly _usageService!: UsageService;
    @inject(I18nService)
    private readonly _i18nService!: I18nService;
    @inject(CommandRegistry)
    private readonly _commandRegistry!: CommandRegistry;
    @inject(CreditService)
    private readonly _creditService!: CreditService;
    @inject(PermissionModel)
    private readonly _permissionModel!: PermissionModel;
    @inject(HeaderService)
    private readonly _headerService!: HeaderService;
    @inject(AiAgentEnvService)
    private readonly _aiAgentEnvService!: AiAgentEnvService;
    @inject(Logger)
    private readonly _logger!: Logger;
    @inject(Message)
    private readonly _message!: Message;
    @inject(RavenService)
    private readonly _ravenService: RavenService;

    /**
     * 子类需要实现此方法来指定如何检查用量
     * @returns 是否还有剩余用量
     */
    protected abstract _hasUsage(usageService: UsageService): boolean;

    /**
     * @see  https://confluence.zhihuiya.com/pages/viewpage.action?pageId=262417400
     * @returns
     */
    async checkPermission(/* context: FeaturePermissionContext */): Promise<PermissionCheckResult> {
        // 1. 检查是否有剩余用量
        if (this._hasUsage(this._usageService)) {
            return {
                granted: true,
            };
        }
        if (this._headerService.account?.siteRegion === 'CN') {
            this._callIframeParent();
            return {
                granted: false,
            };
        }
        // 2. 获取用户套餐类型
        const planType = this._permissionModel.modelData.tierCode;
        if (planType === 'ENTERPRISE') {
            this._callIframeParent();
            return {
                granted: false,
            };
        }
        // 3. FREE 用户：打开定价弹窗
        if (planType === 'FREE') {
            this.tracking();
            this._commandRegistry.executeCommand('pricing:open-dialog');
            return {
                granted: false,
            };
        }

        // 4. PRO 用户：打开联系销售弹窗
        if (planType === 'PRO') {
            this.tracking();
            this._commandRegistry.executeCommand('hubspot:contact-us', {
                title: this._i18nService.t('pricing.contactUsTitle'),
            });
            return {
                granted: false,
            };
        }
        return {
            granted: false,
        };

    }

    private tracking(){
        this._ravenService.track(TrackEvents.AIR_USAGE_UPGRADE_SHOW, {
            eventType: TRACK_EVENT_TYPE.EurekaUpgradePage,
            eventDetailStr: {
                use_where: 'ip',
            },
        });
    }


    private _callIframeParent(): Promise<boolean | undefined> {
        if (!this._aiAgentEnvService.isInIframe) {
            this._logger.warn('不在 iframe 中，简单出错提示');
            this._message.error({
                message: this._i18nService.t('drafting.errors.countOverLimit'),
            });
            return Promise.resolve(undefined);
        }
        const eventName = 'usage-limit';
        return new Promise((resolve) => {
            if (this._aiAgentEnvService.isInIframe) {
                const remove = () => {
                    window.removeEventListener('message', handler);
                    window.clearTimeout(timer);
                };
                const timer = setTimeout(() => {
                    remove();
                    resolve(undefined);
                }, 5000);
                const handler = (event: MessageEvent) => {
                    const data = JSON.parse(event.data);
                    if (data.type === eventName) {
                        resolve(data.data);
                        remove();
                    }
                };
                window.addEventListener('message', handler);
                window.parent.postMessage(JSON.stringify({ type: eventName }), '*');
            }
        });
    }

    private _showToast() {
        this._message.error({
            message: this._i18nService.t('drafting.errors.countOverLimit'),
        });
    }

    async recordUsage(context: FeaturePermissionContext): Promise<void> {
        console.log(`[${this.featureId}] 使用量已记录`, context.featureId);
        // 同步用量数据
        this._creditService.syncCreditUsage();
    }
}
